import { Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ReplaySubject, Subscription } from 'rxjs';
import { FieldGroupModel, FieldGroupPlacement } from 'src/shared/models/field/field-group';
import { AuthenticatedHttpService, OfflineWrapResult } from './authenticatedHttpService.service';
import { Option } from 'src/shared/models/field/option';
import { environment } from 'src/environments/environment';
import { LocalStorageService } from './local-storage.service';
import { FieldModel, FieldType, TextFieldVariant } from 'src/shared/models/field/field';
import { dateRangeDelimiter } from 'src/constants/defaults';
import { SentryLogProvider } from './sentry/sentry.provider';
import * as fp from 'lodash/fp';

@Injectable({ providedIn: 'root' })
export class FieldProvider {
  static FIELDS = 'FieldProvider_FIELDS';

  private _loading = signal(false);
  public readonly loading = this._loading.asReadonly();
  public readonly loading$ = toObservable(this._loading);

  private _fieldGroups = new ReplaySubject<Array<FieldGroupModel>>(1);
  public fieldGroups$ = this._fieldGroups.asObservable();

  initRequired: boolean | null = true;

  sub: Subscription;
  private _requestPageEnabled = signal(false); // Optimization
  public readonly requestPageEnabled = this._requestPageEnabled.asReadonly();
  public readonly requestPageEnabled$ = toObservable(this._requestPageEnabled);

  private fillPageEnabled(fieldGroups: Array<FieldGroupModel>) {
    const fgs = (fieldGroups || []).filter(
      (f) => f.placement === FieldGroupPlacement.CreateNotificationRequestPage
    );

    const options = fgs
      .map((fg) => fg.fields || [])
      .reduce((acc, cur) => [...acc, ...cur], [])
      .map((f) => f.options || [])
      .reduce((acc, cur) => [...acc, ...cur], []) as Option[];

    const optionIds = options.map((o) => o.id);
    const fgOptions = fgs.map((fg) => fg.optionId).filter((o) => o);

    const matched = fp.intersection(optionIds, fgOptions);
    this._requestPageEnabled.set(matched.length > 0);

    this.sentry.trace('fields:requestPageEnabled', {
      enabled: matched.length > 0,
      requestGroupCount: fgs.length,
      groupOptionIds: fgOptions,
      fieldOptionIds: optionIds,
      matchedOptionIds: matched,
      orphanGroupOptionIds: fgOptions.filter((o) => !optionIds.includes(o!)),
    });
  }

  private emitCount = 0;

  private describe(fieldGroups: Array<FieldGroupModel>) {
    return (fieldGroups || []).map((g) => ({
      id: g.id,
      placement: g.placement,
      order: g.order,
      optionId: g.optionId ?? null,
      fields: (g.fields || []).map((f) => ({
        id: f.id,
        name: f.name,
        type: f.type,
        order: f.order,
        options: (f.options || []).map((o) => o.id),
      })),
    }));
  }

  private emitFieldGroups(fieldGroups: Array<FieldGroupModel>, source: string) {
    this.emitCount++;
    this.sentry.trace('fields:emit', {
      emitNumber: this.emitCount,
      source,
      groupCount: (fieldGroups || []).length,
      groups: this.describe(fieldGroups),
    });
    this._fieldGroups.next(fieldGroups);
  }

  constructor(
    private http: AuthenticatedHttpService,
    private storage: LocalStorageService,
    private sentry: SentryLogProvider
  ) {
    this.sub = this.fieldGroups$.subscribe((fgs) => this.fillPageEnabled(fgs));
  }

  async init(hideCurrent = true) {
    try {
      this._loading.set(true);
      this.initRequired = null;

      this.sentry.trace('fields:init:start', { hideCurrent, emitCountSoFar: this.emitCount });

      if (hideCurrent)
        // So that user doesn't interact with 'reloading controls'
        this.emitFieldGroups([], 'init:clear');

      const res = await this.http.wrapIntoOffline<Array<FieldGroupModel>>({
        fetch: this.http.get,
        params: [environment.web.identity + '/api/fields'],
        storeKey: FieldProvider.FIELDS,
      });

      this.sentry.trace('fields:init:response', {
        offline: res.status === OfflineWrapResult.Offline,
        status: res.status,
        rawGroupCount: (res.result || []).length,
        rawGroups: (res.result || []).map((g: any) => ({
          id: g.id,
          placement: g.placement,
          optionId: g.optionId ?? null,
          fieldCount: (g.fields || []).length,
        })),
      });

      const normalizeName = (name: string): string =>
        (name ?? '').trim().toLowerCase().replace(/:$/, '');

      const fields = (res.result || []).map((f) => {
        const group = FieldGroupModel.fromAny(f);

        this.replaceVonBisWithRangeField(group);

        this.removeUnknownFieldTypes(group);

        this.sortFieldsByOrder(group);

        return group;
      });

      this.emitFieldGroups(fields, 'init:data');
      this.initRequired = res.status === OfflineWrapResult.Offline;
    } catch (error) {
      this.sentry.reportFlowError('fields', 'fields:init:failed', error, {
        emitCountSoFar: this.emitCount,
      });
      throw error;
    } finally {
      this._loading.set(false);
    }
  }

  async purge() {
    this.sentry.trace('fields:purge', { emitCountBeforePurge: this.emitCount });

    this._fieldGroups.complete();
    this._fieldGroups = new ReplaySubject<Array<FieldGroupModel>>(1);
    this.fieldGroups$ = this._fieldGroups.asObservable();
    this.emitCount = 0;

    await this.storage.remove(FieldProvider.FIELDS);
    this.initRequired = null;
  }

  private replaceVonBisWithRangeField(group: FieldGroupModel): void {
    const dateFields = group.fields?.filter((f) => f.type === FieldType.DateTime) || [];

    const von = dateFields.find(
      (f) =>
        this.normalizeName(f.name).startsWith('von') || this.normalizeName(f.name).endsWith('von')
    );
    const bis = dateFields.find(
      (f) =>
        this.normalizeName(f.name).startsWith('bis') || this.normalizeName(f.name).endsWith('bis')
    );

    if (!von || !bis) return;

    const hasTime =
      (von.pickerFormat ?? '').includes('HH') || (bis.pickerFormat ?? '').includes('HH');

    const rangeType = hasTime ? FieldType.DateTimeRange : FieldType.DateRange;

    const newRangeField = new FieldModel(
      von.id,
      von.order,
      rangeType,
      `${von.name}${dateRangeDelimiter}${bis.name}`,
      '',
      '',
      '',
      von.creator,
      von.title,
      von.createdAt,
      von.isRead,
      TextFieldVariant.String,
      [],
      von.displayFormat,
      von.pickerFormat
    );

    group.fields = group.fields?.filter((f) => f !== von && f !== bis);
    group.fields.push(newRangeField);
  }

  private removeUnknownFieldTypes(group: FieldGroupModel): void {
    group.fields = group.fields?.filter((f) => Object.values(FieldType).includes(f.type));
  }

  private sortFieldsByOrder(group: FieldGroupModel): void {
    group.fields?.sort((a, b) => a.order - b.order);
  }

  private normalizeName(name: string): string {
    return (name ?? '').trim().toLowerCase().replace(/:$/, '');
  }

  ionViewWillLeave(): void {
    if (this.sub) this.sub.unsubscribe();
  }
}
